import { IconCheckFill } from "@/assets/icons/geist/IconCheckFill";
import { IconWarning } from "@/assets/icons/geist/IconWarning";
import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { cn } from "@/components/podkit/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes, type ReactElement } from "react";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";

const BannerVariant = cva(
    cn("flex justify-center items-center gap-1 rounded-lg border-[0.5px] border-border-base p-[10px]"),
    {
        variants: {
            variant: {
                danger: "bg-surface-red",
                warning: "bg-surface-yield",
                success: "bg-surface-green",
                info: "bg-surface-primary",
                progress: "bg-surface-primary",
            },
        },
        defaultVariants: {
            variant: "danger",
        },
    },
);

export type BannerVariant = VariantProps<typeof BannerVariant>["variant"];

export type BannerProps = {
    text: string | ReactElement;
    truncate?: boolean;
} & VariantProps<typeof BannerVariant> &
    HTMLAttributes<HTMLDivElement>;

export const Banner = forwardRef<HTMLDivElement, BannerProps>(
    ({ text, variant, className, truncate, ...props }, ref) => {
        let iconClassName = "";
        switch (variant) {
            case "danger":
                iconClassName = "text-content-red";
                break;
            case "warning":
                iconClassName = "text-content-yield";
                break;
            case "success":
                iconClassName = "text-content-positive";
                break;
            case "info":
                iconClassName = "text-content-primary";
                break;
            case "progress":
                iconClassName = "animate-spin text-content-primary";
                break;
        }

        let icon = <IconWarning size="sm" className={cn(iconClassName, "shrink-0")} />;
        if (variant === "success") {
            icon = <IconCheckFill size="sm" className={cn(iconClassName, "shrink-0")} />;
        } else if (variant === "info") {
            icon = <IconInfo size="sm" className={cn(iconClassName, "shrink-0")} />;
        } else if (variant === "progress") {
            icon = <IconSpinner size="sm" className={cn(iconClassName, "shrink-0")} />;
        }

        return (
            <div
                ref={ref}
                className={cn(BannerVariant({ variant }), { "overflow-hidden": truncate }, className)}
                {...props}
            >
                {icon}
                <div
                    className={cn("text-sm font-bold text-content-primary", { truncate: truncate })}
                    title={typeof text === "string" && truncate ? text : undefined}
                >
                    {text}
                </div>
            </div>
        );
    },
);
Banner.displayName = "Banner";
